import type { APIData } from '../../../core/types/APIData';
import type { BattleTest } from '../../../core/types/Battle';
import { Deal, Promo } from '../../../core/types/Deal';
import { QueryParams } from '../../../utils/encodeQueryString';
import { getSearchAPIEndpoint } from '../../../utils/getSearchAPIEndpoint';
import { ApiResponse, GetJSON } from '../apiUtils';

export type WidgetResponse = {
  widget: {
    data: APIData;
  };
  battle: BattleTest | null;
  area: string | null;
  models: Record<string, number>;
  version: string;
};

type ApiPromo = Omit<Promo, 'value'> & { value: string | boolean };

export type ApiDeal = Omit<Deal, 'promos'> & { promos?: ApiPromo[] };

export type WidgetApiResponse = Omit<WidgetResponse, 'widget'> & {
  widget: { data: Omit<APIData, 'offers'> & { offers: ApiDeal[] } };
};

export const widgetApiResponseToWidgetResponse = (
  response: ApiResponse<WidgetApiResponse>,
): ApiResponse<WidgetResponse> => {
  if (response.ok) {
    return {
      ...response,
      data: {
        ...response.data,
        widget: {
          ...response.data.widget,
          data: {
            ...response.data.widget.data,
            offers: response.data.widget.data.offers.map((offer) => ({
              ...offer,
              promos: offer.promos?.map((promo) => ({
                ...promo,
                value: String(promo.value),
              })),
            })),
          },
        },
      },
    };
  }
  return response;
};

export const createWidgetResponseGetter = (getJSON: GetJSON<WidgetApiResponse>) => {
  return async (params?: QueryParams): Promise<ApiResponse<WidgetResponse>> => {
    const response = await getJSON(`${getSearchAPIEndpoint()}/widget.php`, params);
    // TODO(Petr): HAWK-7012 Very basic API data validation to satisfy existing tests.
    //  Add proper schema validation later if we decide we don't trust Search API.
    if (response.ok && !response.data.widget.data) {
      return {
        ok: false,
        status: 0,
        detail: 'Invalid API data format',
      };
    }
    return widgetApiResponseToWidgetResponse(response);
  };
};

export type WidgetResponseGetter = ReturnType<typeof createWidgetResponseGetter>;
